import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgSelectModule } from '@ng-select/ng-select';

import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular";

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { NgChartsModule } from 'ng2-charts';
import { CookieService } from 'ngx-cookie-service';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { VgBufferingModule } from '@videogular/ngx-videogular/buffering';
import { VgControlsModule } from '@videogular/ngx-videogular/controls';
import { VgCoreModule } from '@videogular/ngx-videogular/core';
import { VgOverlayPlayModule } from '@videogular/ngx-videogular/overlay-play';

import { BasicAuthorizationInterceptor } from './core/interceptors/basic-authorization.interceptor';
import { ClasslinkAuthorizationInterceptor } from './core/interceptors/classlink-authorization.interceptor';
import { CleverAuthorizationInterceptor } from './core/interceptors/clever-authorization.interceptor';
import { ErrorReportingInterceptor } from './core/interceptors/error-reporting.interceptor.ts.interceptor';
import { PercentRoundPipe } from './core/pipes/display-percent-rounded-pipe';
import { BatchEnrollmentComponent } from './enrollment/batch-enrollment/batch-enrollment.component';
import { EnrollmentComponent } from './enrollment/student-enrollment/enrollment.component';
import { SecureDataSharingComponent } from './files/secure-data-sharing/secure-data-sharing.component';
import { LoginsModule } from './logins/logins.module';
import { DistrictDashboardComponent } from './reports/district/dashboard/dashboard.component';
import { DistrictDiagnosticComponent } from './reports/district/diagnostic/diagnostic.component';
import { DistrictScreenerComponent } from './reports/district/screener/screener.component';
import { DistrictUsageComponent } from './reports/district/usage/usage.component';
import { SchoolDashboardComponent } from './reports/school/dashboard/school-dashboard.component';
import { ScreenerComponent } from './reports/screener/screener.component';
import { AssignCoTeacherComponent } from './reports/system/coteacher/coteacher.component';
import { ParentDashboardComponent } from './reports/system/dashboards/parent/parent-dashboard.component';
import { SystemDashboardComponent } from './reports/system/dashboards/system/system-dashboard.component';
import { DiagnosticDetailComponent } from './reports/system/diagnostic/detail/detail.component';
import { DiagnosticReportComponent } from './reports/system/diagnostic/diagnostic.component';
import { ObjectivePrePostComponent } from './reports/system/objective/objective.component';
import { GroupSummaryComponent } from './reports/system/summary/group/group.component';
import { SessionSummaryComponent } from './reports/system/summary/session/session.component';
import { StudentSummaryComponent } from './reports/system/summary/student/student.component';
import { UnitDetailComponent } from './reports/system/unit/detail/unit-detail.component';
import { ResourcesCategoryComponent } from './resources/category-select/category-select.component';
import { HtmlResourcesComponent } from './resources/html/html.component';
import { KnowledgeBaseItemComponent } from './resources/knowledge-base/item/item.component';
import { KnowledgeBaseComponent } from './resources/knowledge-base/knowledge-base.component';
import { PdfViewerDialogComponent } from './resources/pdf-viewer/pdf-viewer-dialog.component';
import { ResourcesHeaderPipe } from './resources/resources-header.pipe';
import { ResourcesComponent } from './resources/resources.component';
import { RosterClasslinkComponent } from './roster-classlink/roster-classlink.component';
import { RosterCleverComponent } from './roster-clever/roster-clever.component';
import { SettingsComponent } from './settings/settings.component';
import { BatchPrintDialogComponent } from './shared/components/batch-print-dialog/batch-print-dialog.component';
import { PdfPreviewComponent } from './shared/components/pdf-preview/pdf-preview.component';
import { SharedModule } from './shared/shared.module';
import { StudentsModule } from './students/students.module';
import { AssignDiagnosticsComponent } from './subscriptions/assign-diagnostics/assign-diagnostics.component';
import { DiagnosticScheduleComponent } from './subscriptions/diagnostic-schedule/diagnostic-schedule.component';
import { ContactComponent } from './support/contact/contact.component';
import { SurveyComponent } from './survey/survey/survey.component';
import { TourComponent } from './shared/components/tour/tour.component';
import { ZonedDateTimePipe } from './core/pipes/zoned-date-time.pipe';
import { ZonePipe } from './shared/pipes/zone.pipe';
import { SmallGroupLessonDisplayPipe } from './reports/system/unit/detail/small-group-lesson-display.pipe';

@NgModule({ declarations: [
        AppComponent,
        ResourcesComponent,
        ResourcesCategoryComponent,
        HtmlResourcesComponent,
        SettingsComponent,
        EnrollmentComponent,
        ScreenerComponent,
        ParentDashboardComponent,
        SystemDashboardComponent,
        RosterCleverComponent,
        ObjectivePrePostComponent,
        SessionSummaryComponent,
        UnitDetailComponent,
        DiagnosticScheduleComponent,
        AssignDiagnosticsComponent,
        DiagnosticReportComponent,
        DiagnosticDetailComponent,
        DistrictScreenerComponent,
        DistrictUsageComponent,
        DistrictDashboardComponent,
        DistrictDiagnosticComponent,
        SchoolDashboardComponent,
        BatchEnrollmentComponent,
        ContactComponent,
        SurveyComponent,
        StudentSummaryComponent,
        AssignCoTeacherComponent,
        KnowledgeBaseComponent,
        KnowledgeBaseItemComponent,
        RosterClasslinkComponent,
        PdfViewerDialogComponent,
        SecureDataSharingComponent
    ],
  bootstrap: [AppComponent],
    imports: [BrowserModule,
        AppRoutingModule,
        ReactiveFormsModule,
        NgSelectModule,
        FormsModule,
        LoginsModule,
        StudentsModule,
        SharedModule,
        FontAwesomeModule,
        NgChartsModule,
        BrowserAnimationsModule,
        NgbModule,
        VgCoreModule,
        VgBufferingModule,
        VgControlsModule,
        VgOverlayPlayModule,
        PercentRoundPipe,
        ResourcesHeaderPipe,
        BatchPrintDialogComponent,
        GroupSummaryComponent,
        PdfPreviewComponent,
        TourComponent,
        ZonedDateTimePipe,
        ZonePipe,
        SmallGroupLessonDisplayPipe,
    ],
        providers: [
            CookieService,
            { provide: HTTP_INTERCEPTORS, useClass: BasicAuthorizationInterceptor, multi: true },
            { provide: HTTP_INTERCEPTORS, useClass: CleverAuthorizationInterceptor, multi: true },
            { provide: HTTP_INTERCEPTORS, useClass: ClasslinkAuthorizationInterceptor, multi: true },
            { provide: HTTP_INTERCEPTORS, useClass: ErrorReportingInterceptor, multi: true },
            AppRoutingModule,
            ReactiveFormsModule,
            {
                provide: ErrorHandler,
                useValue: Sentry.createErrorHandler({
                    showDialog: false,
                }),
            }, {
                provide: Sentry.TraceService,
                deps: [Router],
            },
            provideAppInitializer(() => {
                // TODO: Review adjusted code after Angular version update
                // const initializerFn = (() => () => { })(inject(Sentry.TraceService));
                inject(Sentry.TraceService);
            }),
            provideHttpClient(withInterceptorsFromDi()),
        ]})
export class AppModule { }
